import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import ChatSelect, { SelectedChat } from "./ChatSelect"
import { isMobile } from "@/utils/detectDevice"

interface ForwardMessageDialogProps {
  open: boolean
  onClose: () => void
  onForward: (chatJid: string) => void
}

const ForwardMessageDialog: React.FC<ForwardMessageDialogProps> = ({
  open,
  onClose,
  onForward,
}) => {
  const { t } = useTranslation()
  const [selectedChat, setSelectedChat] = useState<SelectedChat | undefined>(
    undefined,
  )
  const [chatSelectOpen, setChatSelectOpen] = useState(false)

  // Auto-open the ChatSelect popover when the dialog opens
  useEffect(() => {
    if (!isMobile) {
      if (open) {
        // Add a small delay to ensure the dialog is fully rendered
        const timer = setTimeout(() => {
          setChatSelectOpen(true)
        }, 100)
        return () => clearTimeout(timer)
      } else {
        setChatSelectOpen(false)
      }
    }
  }, [open])

  const handleForward = () => {
    if (selectedChat) {
      onForward(selectedChat.chatJid)
      onClose()
      setSelectedChat(undefined)
    }
  }

  const handleClose = () => {
    onClose()
    setSelectedChat(undefined)
    setChatSelectOpen(false)
    // Fix for pointer events being disabled after modal closes
    setTimeout(() => {
      document.body.style.pointerEvents = ""
    }, 500)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose} modal>
      <DialogContent className="top-0 mt-0 translate-y-0 sm:top-1/2 sm:mt-auto sm:max-w-md sm:-translate-y-1/2">
        <DialogHeader>
          <DialogTitle>{t("chat.forward.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("chat.forward.selectChat")}
            </label>
            <ChatSelect
              value={selectedChat?.chatJid}
              onValueChange={setSelectedChat}
              placeholder={t("chat.forward.placeholder")}
              open={chatSelectOpen}
              onOpenChange={setChatSelectOpen}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleForward}
            disabled={!selectedChat}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {t("chat.forward.forward")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ForwardMessageDialog
